import React, { useEffect, useState } from "react";
import "./AddAClientModal.css";
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ButtonCancel,
  ButtonSave,
} from "../../Buttons/ButtonGroup/ButtonGroup";
import { connect } from "react-redux";
import {
  Input,
  MaskedTextBox,
  NumericTextBox,
  Switch,
} from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import ClientStatus from "../../../data/client_status.json";
import Button from "@mui/material/Button";
import { saveClient, saveCoClient } from "../../../api/advisor/client";
import { formatTimestampToDate } from "../../../common/formatter";
import {
  isEmailValid,
  isPhoneNumberValid,
  validateClientAndCoClient,
} from "../../../common/validation";
import { getAdvisorDefaultPlanSettings } from "../../../api/advisor/defaultPlanSettings";
import CustomDatePicker from "../../Inputs/CustomDatePicker/CustomDatePicker";
import AddIcon from "@mui/icons-material/Add";
import country_list from "../../../data/country_list.json";
import states_list from "../../../data/us_states.json";
import { LeavePageModal } from "../ConfirmModal/ConfirmModal";

const MALE = "Male";
const FEMALE = "Female";
const NON_BINARY = "Non Binary";
const GENDER_OTHER = "Other";
const PREFER_NOT_TO_SAY = "Prefer Not to Say";

const AddAClientModal = (props) => {
  const { t } = useTranslation();

  const currentUser = props.currentUser.toJS();

  const GENDER_DDL = [
    MALE,
    FEMALE,
    NON_BINARY,
    GENDER_OTHER,
    PREFER_NOT_TO_SAY,
  ];

  let defaultRetirementAge = 67;
  let defaultLifeExpectancyAge = 95;

  useEffect(() => {
    getAdvisorDefaultPlanSettings(currentUser.advisor.advisorId).then(
      (response) => {
        defaultRetirementAge =
          response.defaultSettings.clientKeyDates.retirementAge;
        defaultLifeExpectancyAge =
          response.defaultSettings.clientKeyDates.lifeExpectancyAge;

        let newState = { ...state };
        newState.client = getMemberTemplate(props.client);
        newState.coClient = getMemberTemplate(props.coClient);
        newState.noNeedShowClientContactInfo = !(
          newState.client.emailAddress === "" &&
          newState.client.phoneNumber === "" &&
          (newState.client.addressLine === "" ||
            newState.client.addressLine == null)
        );
        newState.noNeedShowCoClientContactInfo = !(
          newState.coClient.emailAddress === "" &&
          newState.coClient.phoneNumber === "" &&
          (newState.coClient.addressLine === "" ||
            newState.coClient.addressLine == null)
        );
        setState(newState);
      }
    );
  }, []);

  const getMemberTemplate = (member) => {
    if (member == null || member.id == null) return getNewCoClientTemplate();

    return {
      firstName: {
        value: member ? member.firstName : "",
        isValid: true,
      },
      lastName: {
        value: member ? member.lastName : "",
        isValid: true,
      },
      middleName: {
        value: member ? member.middleName : "",
        isValid: true,
      },
      gender: {
        value: member ? member.gender : MALE,
        isValid: true,
      },
      dateOfBirth: {
        value: member ? new Date(member.dateOfBirth) : new Date(),
        isValid: true,
      },
      status: {
        value: member && member.status ? member.status : ClientStatus.status[0],
        isValid: true,
      },
      retirementAge: {
        value: member ? member.retirementAge : defaultRetirementAge,
        isValid: true,
      },
      lifeExpectancy: {
        value: member ? member.lifeExpectancyAge : defaultLifeExpectancyAge,
        isValid: true,
      },
      emailAddress:
        member && member.emailList ? member.emailList[0].emailAddress : "",
      phoneNumber:
        member && member.phoneList ? member.phoneList[0].phoneNumber : "",
      addressLine:
        member && member.addressList ? member.addressList[0].addressLine : "",
      city: member && member.addressList ? member.addressList[0].city : "",
      zipCode:
        member && member.addressList ? member.addressList[0].zipCode : "",
      state:
        member && member.addressList
          ? member.addressList[0].state
          : {
              name: "Alabama",
              "alpha-2": "AL",
            },
      country:
        member && member.addressList
          ? member.addressList[0].country
          : {
              id: "USA",
              name: "United States",
            },
    };
  };

  const getNewCoClientTemplate = () => {
    return {
      firstName: {
        value: "",
        isValid: true,
      },
      lastName: {
        value: "",
        isValid: true,
      },
      middleName: {
        value: "",
        isValid: true,
      },
      gender: {
        value: MALE,
        isValid: true,
      },
      dateOfBirth: {
        value: new Date(),
        isValid: true,
      },
      status: {
        value: ClientStatus.status[0],
        isValid: true,
      },
      retirementAge: {
        value: defaultRetirementAge,
        isValid: true,
      },
      lifeExpectancy: {
        value: defaultLifeExpectancyAge,
        isValid: true,
      },
      emailAddress: "",
      phoneNumber: "",
      addressLine: "",
      city: "",
      zipCode: "",
      state: {
        name: "Alabama",
        "alpha-2": "AL",
      },
      country: {
        id: "USA",
        name: "United States",
      },
      hasHealthInsurance: {
        value: false,
        isValid: true,
      },
      hasDisabilityInsurance: {
        value: false,
        isValid: true,
      },
      hasWill: {
        value: false,
        isValid: true,
      },
      hasTrust: {
        value: false,
        isValid: true,
      },
      isBeneficiariesUpToDate: {
        value: false,
        isValid: true,
      },
    };
  };

  const [state, setState] = useState({
    client: getMemberTemplate(props.client),
    coClient: getMemberTemplate(props.coClient),
    needCoClient:
      props.coClient != null && props.coClient.id != null ? true : false,
    showClientContactInfo: false,
    showCoClientContactInfo: false,
    noNeedShowClientContactInfo: false,
    noNeedShowCoClientContactInfo: false,
    step: 1,
  });

  const update = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey][subKey].value = value;
    newState[primaryKey][subKey].isValid = true;
    setState(newState);
  };

  const updateContact = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey][subKey] = value;
    if (subKey === "country") {
      if (value.id === "USA") {
        newState[primaryKey]["state"] = { name: "Alabama", "alpha-2": "AL" };
      } else {
        newState[primaryKey]["state"] = "";
      }
    }
    setState(newState);
  };

  const updateDateOfBirth = (primaryKey, subKey) => {
    return (e) => {
      let newState = { ...state };
      newState[primaryKey][subKey].value = e.target.value;
      newState[primaryKey][subKey].isValid = true;
      setState(newState);
    };
  };

  const save = () => {
    if (validate()) {
      const clientData = generateNewClientParams();

      let coClientData = null;
      if (state.needCoClient) {
        coClientData = generateNewCoClientParams();
      }

      saveClient(currentUser.advisor.advisorId, clientData).then((client) => {
        const clientId = client.id;
        if (coClientData) {
          saveCoClient(
            currentUser.advisor.advisorId,
            clientId,
            coClientData
          ).then((coClient) => {
            props.save(client, coClient);
          });
        } else {
          props.save(client);
        }
      });
    }
  };

  const next = () => {
    if (validate()) {
      setState({
        ...state,
        step: 2,
      });
    }
  };

  const back = () => {
    setState({
      ...state,
      step: 1,
    });
  };

  const validate = () => {
    let newState = { ...state };
    newState["client"] = validateClientAndCoClient(newState["client"]);
    let isValid =
      newState["client"]["firstName"].isValid &&
      newState["client"]["lastName"].isValid &&
      newState["client"]["retirementAge"].isValid &&
      newState["client"]["lifeExpectancy"].isValid &&
      (isEmailValid(newState["client"].emailAddress) ||
        newState["client"].emailAddress === "") &&
      isPhoneNumberValid(newState["client"].phoneNumber);
    if (state.needCoClient) {
      newState["coClient"] = validateClientAndCoClient(newState["coClient"]);
      isValid =
        newState["client"]["firstName"].isValid &&
        newState["client"]["lastName"].isValid &&
        newState["client"]["retirementAge"].isValid &&
        newState["client"]["lifeExpectancy"].isValid &&
        newState["coClient"]["firstName"].isValid &&
        newState["coClient"]["lastName"].isValid &&
        newState["coClient"]["retirementAge"].isValid &&
        newState["coClient"]["lifeExpectancy"].isValid &&
        (isEmailValid(newState["coClient"].emailAddress) ||
          newState["coClient"].emailAddress === "") &&
        isPhoneNumberValid(newState["coClient"].phoneNumber);
    }
    setState(newState);

    return isValid;
  };

  const generateNewClientParams = () => {
    if (props.client != null && props.client.id != null) {
      let client = { ...props.client };
      client.clientId = props.client.id;
      client.firstName = state.client.firstName.value;
      client.middleName = state.client.middleName.value;
      client.lastName = state.client.lastName.value;
      client.dateOfBirth = formatTimestampToDate(
        state.client.dateOfBirth.value.getTime(),
        "MM/dd/yyyy"
      );
      client.gender = state.client.gender.value;
      client.status = state.client.status.value;
      client.retirementAge = state.client.retirementAge.value;
      client.lifeExpectancyAge = state.client.lifeExpectancy.value;
      client.addressList[0] = {
        addressId: client.addressList[0].addressId,
        country: state.client.country.id,
        addressLine: state.client.addressLine,
        city: state.client.city,
        zipCode: state.client.zipCode,
        state:
          state.client.country.id === "USA"
            ? state.client.state["alpha-2"]
            : state.client.state,
      };
      client.emailList[0] = {
        emailAddressId: client.emailList[0].emailAddressId,
        emailAddressType: "HOME",
        emailAddress: state.client.emailAddress,
      };
      client.phoneList[0] = {
        phoneNumberId: client.phoneList[0].phoneNumberId,
        phoneNumberType: "PERSONAL",
        phoneNumber: state.client.phoneNumber,
      };
      return client;
    } else {
      return {
        clientId: null,
        personId: null,
        firstName: state.client.firstName.value,
        middleName: state.client.middleName.value,
        lastName: state.client.lastName.value,
        salutation: null,
        dateOfBirth: formatTimestampToDate(
          state.client.dateOfBirth.value.getTime(),
          "MM/dd/yyyy"
        ),
        gender: state.client.gender.value,
        company: {
          companyId: null,
          name: "",
          website: "",
        },
        notes: null,
        retirementAge: state.client.retirementAge.value,
        retirementMonth: "Jan",
        lifeExpectancyAge: state.client.lifeExpectancy.value,
        lifeExpectancyMonth: "Jan",
        status: state.client.status.value,
        relationshipType: "CLIENT",
        addressList: [
          {
            addressId: null,
            country: state.client.country.id,
            addressLine: state.client.addressLine,
            city: state.client.city,
            zipCode: state.client.zipCode,
            state:
              state.client.country.id === "USA"
                ? state.client.state["alpha-2"]
                : state.client.state,
          },
        ],
        emailList: [
          {
            emailAddressId: null,
            emailAddressType: "HOME",
            emailAddress: state.client.emailAddress,
          },
        ],
        phoneList: [
          {
            phoneNumberId: null,
            phoneNumberType: "PERSONAL",
            phoneNumber: state.client.phoneNumber,
          },
        ],
        hasHealthInsurance: state.client.hasHealthInsurance
          ? state.client.hasHealthInsurance.value
          : false,
        hasDisabilityInsurance: state.client.hasDisabilityInsurance
          ? state.client.hasDisabilityInsurance.value
          : false,
        hasWill: state.client.hasWill ? state.client.hasWill.value : false,
        hasTrust: state.client.hasTrust ? state.client.hasTrust.value : false,
        isBeneficiariesUpToDate: state.client.isBeneficiariesUpToDate
          ? state.client.isBeneficiariesUpToDate.value
          : false,
      };
    }
  };

  const generateNewCoClientParams = () => {
    if (props.coClient != null && props.coClient.id != null) {
      let coClient = { ...props.coClient };
      coClient.coClientId = props.coClient.id;
      coClient.firstName = state.coClient.firstName.value;
      coClient.middleName = state.coClient.middleName.value;
      coClient.lastName = state.coClient.lastName.value;
      coClient.dateOfBirth = formatTimestampToDate(
        state.coClient.dateOfBirth.value.getTime(),
        "MM/dd/yyyy"
      );
      coClient.gender = state.coClient.gender.value;
      coClient.status = state.coClient.status.value;
      coClient.retirementAge = state.coClient.retirementAge.value;
      coClient.lifeExpectancyAge = state.coClient.lifeExpectancy.value;
      coClient.addressList[0] = {
        addressId: coClient.addressList[0].addressId,
        country: state.coClient.country.id,
        addressLine: state.coClient.addressLine,
        city: state.coClient.city,
        zipCode: state.coClient.zipCode,
        state:
          state.coClient.country.id === "USA"
            ? state.coClient.state["alpha-2"]
            : state.coClient.state,
      };
      coClient.emailList[0] = {
        emailAddressId: coClient.emailList[0].emailAddressId,
        emailAddressType: "HOME",
        emailAddress: state.coClient.emailAddress,
      };
      coClient.phoneList[0] = {
        phoneNumberId: coClient.phoneList[0].phoneNumberId,
        phoneNumberType: "PERSONAL",
        phoneNumber: state.coClient.phoneNumber,
      };
      return coClient;
    } else {
      return {
        coClientId: null,
        personId: null,
        firstName: state.coClient.firstName.value,
        middleName: state.coClient.middleName.value,
        lastName: state.coClient.lastName.value,
        salutation: null,
        dateOfBirth: formatTimestampToDate(
          state.coClient.dateOfBirth.value.getTime(),
          "MM/dd/yyyy"
        ),
        gender: state.coClient.gender.value,
        company: {
          companyId: null,
          name: "",
          website: "",
        },
        notes: null,
        retirementAge: state.coClient.retirementAge.value,
        retirementMonth: "Jan",
        lifeExpectancyAge: state.coClient.lifeExpectancy.value,
        lifeExpectancyMonth: "Jan",
        relationshipType: "COCLIENT",
        addressList: [
          {
            addressId: null,
            country: state.coClient.country.id,
            addressLine: state.coClient.addressLine,
            city: state.coClient.city,
            zipCode: state.coClient.zipCode,
            state:
              state.coClient.country.id === "USA"
                ? state.coClient.state["alpha-2"]
                : state.coClient.state,
          },
        ],
        emailList: [
          {
            emailAddressId: null,
            emailAddressType: "HOME",
            emailAddress: state.coClient.emailAddress,
          },
        ],
        phoneList: [
          {
            phoneNumberId: null,
            phoneNumberType: "PERSONAL",
            phoneNumber: state.coClient.phoneNumber,
          },
        ],
      };
    }
  };

  const addCoClient = () => {
    let coClient =
      state.coClient == null
        ? getMemberTemplate(props.coClient)
        : state.coClient;
    setState({
      ...state,
      coClient: coClient,
      needCoClient: true,
    });
  };

  const removeCoClient = () => {
    setState({
      ...state,
      needCoClient: false,
    });
  };

  const addContactInfo = (type) => {
    if (type === "client") {
      setState({
        ...state,
        showClientContactInfo: true,
      });
    } else if (type === "coClient") {
      setState({
        ...state,
        showCoClientContactInfo: true,
      });
    }
  };

  const hideContactInfo = (type) => {
    if (type === "client") {
      setState({
        ...state,
        showClientContactInfo: false,
      });
    } else if (type === "coClient") {
      setState({
        ...state,
        showCoClientContactInfo: false,
      });
    }
  };

  const setupStateComponent = (type) => {
    let clientStateField;
    if (state[type].country.id === "USA") {
      clientStateField = (
        <FormControl margin="normal" required className="percent-90-width">
          <DropDownList
            label={t("Global.state")}
            value={state[type].state}
            onChange={(event) =>
              updateContact(type, "state", event.target.value)
            }
            data={states_list}
            dataItemKey={"alpha-2"}
            textField={"name"}
          />
        </FormControl>
      );
    } else {
      clientStateField = (
        <FormControl margin="normal" required className="percent-90-width">
          <Input
            label={t("Global.state")}
            value={state[type].state}
            minLength={0}
            maxLength={30}
            onChange={(event) =>
              updateContact(type, "state", event.target.value)
            }
          />
        </FormControl>
      );
    }

    return clientStateField;
  };

  const setupContactInfoComponent = (type) => {
    if (state.noNeedShowClientContactInfo && type === "client") return null;
    if (state.noNeedShowCoClientContactInfo && type === "coClient") return null;

    let contactComponents;
    if (
      (type === "client" && !state.showClientContactInfo) ||
      (type === "coClient" && !state.showCoClientContactInfo)
    ) {
      contactComponents = (
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => addContactInfo(type)}
          >
            {t("Clients.add_contact_info")}
          </Button>
        </Grid>
      );
    } else {
      contactComponents = (
        <React.Fragment>
          <Grid item xs={12} style={{ marginTop: "20px" }}>
            <Typography
              component={"p"}
              variant={"body1"}
              color={"primary"}
              style={{ cursor: "pointer" }}
              onClick={() => hideContactInfo(type)}
            >
              {t("Clients.contact_info_optional_fields")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-90-width">
              <Input
                name="email"
                label={t("Global.email")}
                value={state[type].emailAddress}
                valid={
                  isEmailValid(state[type].emailAddress) ||
                  state[type].emailAddress === ""
                }
                onChange={(event) =>
                  updateContact(type, "emailAddress", event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-90-width">
              <MaskedTextBox
                name="phone"
                label={t("Global.phone")}
                mask={"000-000-0000"}
                value={state[type].phoneNumber}
                valid={isPhoneNumberValid(state[type].phoneNumber)}
                onChange={(event) =>
                  updateContact(type, "phoneNumber", event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl margin="normal" required className="percent-95-width">
              <Input
                name="address"
                label={t("Global.address")}
                value={state[type].addressLine}
                maxLength={250}
                onChange={(event) =>
                  updateContact(type, "addressLine", event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-90-width">
              <Input
                name="city"
                label={t("Global.city")}
                value={state[type].city}
                minLength={0}
                maxLength={50}
                onChange={(event) =>
                  updateContact(type, "city", event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            {setupStateComponent(type)}
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-90-width">
              <Input
                name="zipCode"
                label={t("Global.zip_code")}
                value={state[type].zipCode}
                minLength={0}
                maxLength={10}
                onChange={(event) =>
                  updateContact(type, "zipCode", event.target.value)
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="normal" required className="percent-90-width">
              <DropDownList
                label={t("Global.country")}
                value={state[type].country}
                onChange={(event) =>
                  updateContact(type, "country", event.target.value)
                }
                data={country_list}
                dataItemKey={"id"}
                textField={"name"}
              />
            </FormControl>
          </Grid>
        </React.Fragment>
      );
    }

    return contactComponents;
  };

  let coClientTitle;
  if (props.coClient == null || props.coClient.id == null) {
    coClientTitle = (
      <Typography
        component={"p"}
        variant={"body1"}
        color={"primary"}
        style={{ cursor: "pointer" }}
        onClick={removeCoClient}
      >
        {t("Clients.remove_coclient")}
      </Typography>
    );
  } else {
    coClientTitle = (
      <Typography component={"p"} variant={"body1"} color={"primary"}>
        {t("Clients.coClient")}
      </Typography>
    );
  }

  let coClientFirstNameComponent;
  if (props.client) {
    coClientFirstNameComponent = (
      <Input
        id="firstname_coclient"
        name="firstname_coclient"
        minLength={0}
        maxLength={30}
        label={t("Clients.first_name")}
        autoFocus
        value={state.coClient.firstName.value}
        valid={state.coClient.firstName.isValid}
        onChange={(event) =>
          update("coClient", "firstName", event.target.value)
        }
      />
    );
  } else {
    coClientFirstNameComponent = (
      <Input
        id="firstname_coclient"
        name="firstname_coclient"
        minLength={0}
        maxLength={30}
        label={t("Clients.first_name")}
        value={state.coClient.firstName.value}
        valid={state.coClient.firstName.isValid}
        onChange={(event) =>
          update("coClient", "firstName", event.target.value)
        }
      />
    );
  }

  let coClientContent;
  if (state.needCoClient) {
    coClientContent = (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {coClientTitle}
        </Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            {coClientFirstNameComponent}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            <Input
              id="lastname_coclient"
              name="lastname_coclient"
              minLength={0}
              maxLength={30}
              label={t("Clients.last_name")}
              value={state.coClient.lastName.value}
              valid={state.coClient.lastName.isValid}
              onChange={(event) =>
                update("coClient", "lastName", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            <Input
              id="middlename_coclient"
              name="middlename_coclient"
              label={t("Clients.middle_name")}
              minLength={0}
              maxLength={30}
              value={state.coClient.middleName.value}
              valid={state.coClient.middleName.isValid}
              onChange={(event) =>
                update("coClient", "middleName", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            <DropDownList
              label={t("Clients.gender")}
              data={GENDER_DDL}
              value={state.coClient.gender.value}
              onChange={(event) =>
                update("coClient", "gender", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} className="date-of-birth-div">
          <FormControl margin="normal" required className="percent-90-width">
            <CustomDatePicker
              label={t("Clients.date_of_birth")}
              selectedDate={new Date(state.coClient.dateOfBirth.value)}
              maxDate={new Date()}
              isEditMode={true}
              update={updateDateOfBirth("coClient", "dateOfBirth")}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            <NumericTextBox
              min={0}
              max={200}
              step={1}
              id="retirement_age_coclient"
              name="retirement_age_coclient"
              label={t("Clients.retirement_age")}
              value={state.coClient.retirementAge.value}
              valid={state.coClient.retirementAge.isValid}
              onChange={(event) =>
                update("coClient", "retirementAge", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl margin="normal" required className="percent-90-width">
            <NumericTextBox
              min={0}
              max={200}
              step={1}
              id="life_expectancy_coclient"
              name="life_expectancy_coclient"
              label={t("Clients.life_expectancy")}
              value={state.coClient.lifeExpectancy.value}
              valid={state.coClient.lifeExpectancy.isValid}
              onChange={(event) =>
                update("coClient", "lifeExpectancy", event.target.value)
              }
            />
          </FormControl>
        </Grid>
        {setupContactInfoComponent("coClient")}
      </Grid>
    );
  } else {
    coClientContent = (
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          style={{
            height: "280px",
            textAlign: "center",
            paddingTop: "40px",
          }}
        >
          <Button
            id="addCoClient"
            variant="contained"
            color="primary"
            style={{
              width: "200px",
            }}
            onClick={addCoClient}
          >
            {t("Clients.add_a_coclient")}
          </Button>
        </Grid>
      </Grid>
    );
  }

  const modalTitle = props.client
    ? t("Clients.edit_client")
    : t("Clients.add_new_client");
  const clientTitle = props.client
    ? t("Clients.client")
    : t("Clients.new_client");
  let buttonsGroup;
  if (props.client) {
    buttonsGroup = (
      <React.Fragment>
        <span>
          <ButtonCancel onClick={props.close} />
        </span>
        <span style={{ marginLeft: "10px" }}>
          <ButtonSave onClick={save} />
        </span>
      </React.Fragment>
    );
  } else {
    if (state.step === 1) {
      buttonsGroup = (
        <React.Fragment>
          <span>
            <ButtonCancel onClick={props.close} />
          </span>
          <span style={{ marginLeft: "10px" }}>
            <Button variant="contained" color="primary" onClick={next}>
              {t("Global.next")}
            </Button>
          </span>
        </React.Fragment>
      );
    } else {
      buttonsGroup = (
        <React.Fragment>
          <span>
            <Button
              variant={"outlined"}
              color="primary"
              onClick={back}
              style={{ marginRight: "10px", color: "#333" }}
            >
              {t("Global.back")}
            </Button>
          </span>
          <span style={{ marginLeft: "10px" }}>
            <ButtonSave onClick={save} />
          </span>
        </React.Fragment>
      );
    }
  }

  let clientFirstNameComponent;
  if (props.client) {
    clientFirstNameComponent = (
      <Input
        id="firstname"
        name="firstname"
        label={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.client.firstName.value}
        valid={state.client.firstName.isValid}
        onChange={(event) => update("client", "firstName", event.target.value)}
      />
    );
  } else {
    clientFirstNameComponent = (
      <Input
        id="firstname"
        name="firstname"
        label={t("Clients.first_name")}
        autoFocus
        minLength={0}
        maxLength={30}
        value={state.client.firstName.value}
        valid={state.client.firstName.isValid}
        onChange={(event) => update("client", "firstName", event.target.value)}
      />
    );
  }

  let content = (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item xs={8}></Grid>
        <Grid item xs={4} style={{ textAlign: "right" }}>
          {buttonsGroup}
        </Grid>
      </Grid>
      <Grid container spacing={3} className="add-new-client-modal-div">
        <Grid item xs={6}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography component={"p"} variant={"body1"} color={"primary"}>
                {clientTitle}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                {clientFirstNameComponent}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <Input
                  id="lastname"
                  name="lastname"
                  label={t("Clients.last_name")}
                  minLength={0}
                  maxLength={30}
                  value={state.client.lastName.value}
                  valid={state.client.lastName.isValid}
                  onChange={(event) =>
                    update("client", "lastName", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <Input
                  id="middlename"
                  name="middlename"
                  label={t("Clients.middle_name")}
                  minLength={0}
                  maxLength={30}
                  value={state.client.middleName.value}
                  valid={state.client.middleName.isValid}
                  onChange={(event) =>
                    update("client", "middleName", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <DropDownList
                  label={t("Clients.gender")}
                  data={GENDER_DDL}
                  value={state.client.gender.value}
                  onChange={(event) =>
                    update("client", "gender", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6} className="date-of-birth-div">
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <CustomDatePicker
                  label={t("Clients.date_of_birth")}
                  selectedDate={new Date(state.client.dateOfBirth.value)}
                  maxDate={new Date()}
                  isEditMode={true}
                  update={updateDateOfBirth("client", "dateOfBirth")}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <DropDownList
                  label={t("Clients.status")}
                  data={ClientStatus.status}
                  textField={"value"}
                  dataItemKey={"id"}
                  value={state.client.status.value}
                  onChange={(event) =>
                    update("client", "status", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <NumericTextBox
                  min={0}
                  max={200}
                  step={1}
                  id="retirement_age"
                  name="retirement_age"
                  label={t("Clients.retirement_age")}
                  value={state.client.retirementAge.value}
                  valid={state.client.retirementAge.isValid}
                  onChange={(event) =>
                    update("client", "retirementAge", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-90-width"
              >
                <NumericTextBox
                  min={0}
                  max={200}
                  step={1}
                  id="life_expectancy"
                  name="life_expectancy"
                  label={t("Clients.life_expectancy")}
                  value={state.client.lifeExpectancy.value}
                  valid={state.client.lifeExpectancy.isValid}
                  onChange={(event) =>
                    update("client", "lifeExpectancy", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            {setupContactInfoComponent("client")}
          </Grid>
        </Grid>
        <Grid item xs={6}>
          {coClientContent}
        </Grid>
      </Grid>
    </React.Fragment>
  );

  let questionAnswerContent;
  if (!props.client) {
    questionAnswerContent = (
      <React.Fragment>
        <Grid container spacing={3}>
          <Grid item xs={8}></Grid>
          <Grid item xs={4} style={{ textAlign: "right" }}>
            {buttonsGroup}
          </Grid>
        </Grid>
        <Grid container spacing={3} className="add-new-client-modal-div">
          <Grid item xs={6} className="question">
            <FormControl margin="normal" required className="percent-90-width">
              <label>
                {t("Clients.add_new_client.health_insurance_question")}
              </label>
            </FormControl>
          </Grid>
          <Grid item xs={6} className="answer">
            <FormControl margin="normal" required className="percent-90-width">
              <Switch
                checked={state.client.hasHealthInsurance.value}
                onChange={(event) =>
                  update("client", "hasHealthInsurance", event.target.value)
                }
                onLabel={""}
                offLabel={""}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} className="question">
            <FormControl margin="normal" required className="percent-90-width">
              <label>
                {t("Clients.add_new_client.disability_insurance_question")}
              </label>
            </FormControl>
          </Grid>
          <Grid item xs={6} className="answer">
            <FormControl margin="normal" required className="percent-90-width">
              <Switch
                checked={state.client.hasDisabilityInsurance.value}
                onChange={(event) =>
                  update("client", "hasDisabilityInsurance", event.target.value)
                }
                onLabel={""}
                offLabel={""}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} className="question">
            <FormControl margin="normal" required className="percent-90-width">
              <label>{t("Clients.add_new_client.will_question")}</label>
            </FormControl>
          </Grid>
          <Grid item xs={6} className="answer">
            <FormControl margin="normal" required className="percent-90-width">
              <Switch
                checked={state.client.hasWill.value}
                onChange={(event) =>
                  update("client", "hasWill", event.target.value)
                }
                onLabel={""}
                offLabel={""}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} className="question">
            <FormControl margin="normal" required className="percent-90-width">
              <label>{t("Clients.add_new_client.trust_question")}</label>
            </FormControl>
          </Grid>
          <Grid item xs={6} className="answer">
            <FormControl margin="normal" required className="percent-90-width">
              <Switch
                checked={state.client.hasTrust.value}
                onChange={(event) =>
                  update("client", "hasTrust", event.target.value)
                }
                onLabel={""}
                offLabel={""}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} className="question">
            <FormControl margin="normal" required className="percent-90-width">
              <label>
                {t("Clients.add_new_client.beneficiaries_up_to_date_question")}
              </label>
            </FormControl>
          </Grid>
          <Grid item xs={6} className="answer">
            <FormControl margin="normal" required className="percent-90-width">
              <Switch
                checked={state.client.isBeneficiariesUpToDate.value}
                onChange={(event) =>
                  update(
                    "client",
                    "isBeneficiariesUpToDate",
                    event.target.value
                  )
                }
                onLabel={""}
                offLabel={""}
              />
            </FormControl>
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }

  let modalContent = state.step === 1 ? content : questionAnswerContent;

  return (
    <Card>
      <CardContent className="add-new-client-modal-container">
        <Typography control={"h1"} variant={"h1"}>
          {modalTitle}
        </Typography>
        {modalContent}
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUserReducer,
  };
};

export default connect(mapStateToProps)(AddAClientModal);
